/**
 * useNodeEditingInteractions - Composite hook
 *
 * Wires together all node editing interaction hooks and connects their
 * callbacks to the application notification system. This hook is intended
 * to be consumed by the Map component or a dedicated NodeEditing container.
 *
 * **Validates: Requirement 16.5** – THE Geometry_Editor SHALL prevent removal
 * of nodes that would make the geometry invalid.
 *
 * Behaviour:
 * - Dispatches a warning notification when a node removal is blocked
 * - Dispatches a warning notification when post-edit validation detects issues
 */

import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { addNotification } from '../store/slices/uiSlice';
import { stopDigitization } from '../store/slices/digitizationSlice';
import { setActiveTool as setDrawingTool } from '../store/slices/drawingSlice';
import { setMeasurementMode } from '../store/slices/measurementSlice';
import { deactivateSplitTool, selectIsEditingGeometry } from '../store/slices/geometryEditingSlice';
import {
  useNodeDeleteInteraction,
  type InvalidRemovalCallback,
  type ValidationWarningCallback,
} from './useNodeDeleteInteraction';

/**
 * useNodeEditingInteractions
 *
 * Composes the node delete interaction hook and connects its callbacks
 * to the Redux notification system so users receive visible feedback
 * when a node removal is blocked due to geometry validity constraints.
 */
export function useNodeEditingInteractions(): void {
  const dispatch = useAppDispatch();
  const isEditingGeometry = useAppSelector(selectIsEditingGeometry);

  useEffect(() => {
    if (!isEditingGeometry) return;
    dispatch(stopDigitization());
    dispatch(setDrawingTool(null));
    dispatch(setMeasurementMode(null));
    dispatch(deactivateSplitTool());
  }, [isEditingGeometry, dispatch]);

  /**
   * Called when a node removal is rejected because it would create
   * an invalid geometry. Dispatches a warning notification.
   */
  const handleInvalidRemoval: InvalidRemovalCallback = useCallback(
    (message: string) => {
      dispatch(
        addNotification({
          type: 'warning',
          message,
        })
      );
    },
    [dispatch]
  );

  /**
   * Called when post-edit geometry validation detects topology issues.
   * Dispatches an info notification with the first error.
   */
  const handleValidationWarning: ValidationWarningCallback = useCallback(
    (errors: string[]) => {
      dispatch(
        addNotification({
          type: 'info',
          message: `Geometry warning: ${errors[0]}${errors.length > 1 ? ` (+${errors.length - 1} more)` : ''}`,
        })
      );
    },
    [dispatch]
  );

  useNodeDeleteInteraction({
    onInvalidRemoval: handleInvalidRemoval,
    onValidationWarning: handleValidationWarning,
  });
}

export default useNodeEditingInteractions;
